import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useCartChanges } from "@/hooks/useAutoRefresh";
import { TRY, NUM } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Panel } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Radio, ShoppingCart, Users, X, ArrowRight, Zap, Lock, MessagesSquare, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";
import { useTitleFlash, silenceBell } from "@/hooks/useCartLive";
import { playNotifySound } from "@/lib/sound";
import { UnreadDot } from "@/components/live/LivePrepBar";

const MODE_SHORT = { genel: "Genel", faturali: "Elk. Faturalı", kdvsiz: "Elk. KDV'siz" };
const initials = (n) => String(n || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toLocaleUpperCase("tr-TR");
const ago = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 5) return "şimdi"; if (s < 60) return `${s} sn önce`; if (s < 3600) return `${Math.floor(s / 60)} dk önce`; return `${Math.floor(s / 3600)} sa önce`;
};

// Tüm açık (canlı) hızlı satış sepetleri — herkes görür, açıp birlikte düzenler; kapatma sahibi/yönetici
export function LiveCartsPanel({ user, currentCartId, onJoin }) {
  const [carts, setCarts] = useState([]);
  const [busy, setBusy] = useState("");
  const prevUnread = useRef(null);
  const load = () => api.get("/pos-carts").then((r) => {
    const rows = r.data || [];
    // Bu ekrandaki sepet dışında bir sepete depodan mesaj geldi → ses + uyarı (bu ekrandaki sepeti terminal uyarır)
    const others = rows.filter((c) => c.id !== currentCartId).reduce((a, c) => a + (c.unread_msgs || 0), 0);
    if (prevUnread.current != null && others > prevUnread.current) {
      const c = rows.find((x) => x.id !== currentCartId && x.unread_msgs > 0);
      playNotifySound(); silenceBell("pos_mesaj");
      toast.message("Depodan yeni mesaj", { description: c ? `${c.owner_name} sepeti · ${c.title || "Perakende müşteri"}` : "", duration: 10000 });
    }
    prevUnread.current = others;
    setCarts(rows);
  }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []); // eslint-disable-line
  useCartChanges(load);
  useTitleFlash("live-carts", carts.filter((c) => c.id !== currentCartId).reduce((a, c) => a + (c.unread_msgs || 0), 0));
  const isAdmin = user?.role === "admin";

  const cancel = async (c) => {
    if (!await confirm(`${c.owner_name} kullanıcısının sepeti kapatılsın mı?\n${c.item_count} kalem silinecek, satış yapılmayacak.`)) return;
    setBusy(c.id);
    try { await api.delete(`/pos-carts/${c.id}`); toast.success("Sepet kapatıldı"); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const join = async (c) => { setBusy(c.id); try { await onJoin(c.id); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); } };

  if (!carts.length) return null;
  return (
    <Panel title="Canlı Sepetler" subtitle="Şu an açık olan hızlı satış sepetleri · birlikte düzenleyebilirsiniz" icon={Radio} testid="live-carts-panel"
      actions={<span className="inline-flex items-center gap-1.5 text-xs font-bold tnum rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5" data-testid="live-carts-count"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />{carts.length} açık sepet</span>}>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {carts.map((c) => {
          const mine = c.owner_id === user?.id;
          const here = c.id === currentCartId;
          const others = (c.editors || []).filter((e) => e.id !== c.owner_id);
          const unread = c.unread_msgs || 0;
          return (
            <div key={c.id} className={cn("relative rounded-2xl border bg-card p-4 shadow-sm transition-all hover:shadow-md", unread ? "border-red-500 ring-2 ring-red-500/40 shadow-red-500/20 shadow-lg" : here ? "border-[#EA5820] ring-1 ring-[#EA5820]/30" : "border-slate-200")} data-testid={`live-cart-${c.id}`}>
              {unread > 0 && (
                <span className="absolute -top-2.5 right-3 inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-2 py-0.5 text-[10px] font-black shadow-md animate-bounce" data-testid={`live-cart-unread-${c.id}`}>
                  <MessagesSquare className="h-3 w-3" />{unread} yeni mesaj
                </span>
              )}
              <div className="flex items-start gap-3">
                <div className={cn("grid h-10 w-10 place-items-center rounded-xl font-head font-black text-sm shrink-0", mine ? "bg-[#0B192C] text-white" : "bg-slate-100 text-slate-700")}>{initials(c.owner_name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-bold text-sm truncate" data-testid={`live-cart-owner-${c.id}`}>{c.owner_name}</span>
                    {mine && <span className="rounded-full bg-[#0B192C] text-white text-[9px] font-bold px-1.5 py-0.5">SİZİN</span>}
                    {here && <span className="rounded-full bg-[#EA5820] text-white text-[9px] font-bold px-1.5 py-0.5" data-testid={`live-cart-here-${c.id}`}>BU EKRANDA</span>}
                    {c.prep_list_id && <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-600 text-white text-[9px] font-bold px-1.5 py-0.5" data-testid={`live-cart-prep-${c.id}`}><ClipboardCheck className="h-2.5 w-2.5" />CANLI HAZIRLIK</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{c.title || <span className="italic">Perakende müşteri</span>}{c.mode && <> · <span className="font-semibold">{MODE_SHORT[c.mode] || c.mode}</span></>}</div>
                  <div className="mt-2 flex items-end justify-between gap-2">
                    <div className="text-[11px] text-muted-foreground tnum inline-flex items-center gap-1"><ShoppingCart className="h-3.5 w-3.5" />{c.item_count} kalem · {NUM(c.qty_total)} adet</div>
                    {c.restricted
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-bold" title="Yönetici sepette — tutar gizli" data-testid={`live-cart-locked-${c.id}`}><Lock className="h-3 w-3" />Yönetici sepette</span>
                      : <div className="font-head font-black tnum text-lg text-[#EA5820] leading-none" data-testid={`live-cart-total-${c.id}`}>{TRY(c.grand_total)}</div>}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 text-[10.5px] text-muted-foreground">
                <span className="truncate">Son: <b className="text-slate-600">{c.updated_by}</b> · {ago(c.updated_at)}</span>
                {others.length > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 font-semibold shrink-0" title={others.map((e) => e.name).join(", ")} data-testid={`live-cart-editors-${c.id}`}><Users className="h-3 w-3" />{others.length} kişi düzenliyor</span>}
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" className={cn("flex-1 h-9 rounded-xl font-bold active:scale-[0.98]", here ? "bg-[#EA5820] hover:bg-[#D84A15]" : "")} variant={here ? "default" : "secondary"} onClick={() => join(c)} disabled={busy === c.id} data-testid={`live-cart-open-${c.id}`}>
                  {here ? <Zap className="h-4 w-4 mr-1.5" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}{here ? "Sepete Devam" : "Sepeti Aç / Katıl"}
                  {unread > 0 && <span className="ml-1.5"><UnreadDot n={unread} /></span>}
                </Button>
                {(mine || isAdmin) && <Button size="sm" variant="ghost" className="h-9 w-9 px-0 rounded-xl text-destructive hover:text-destructive hover:bg-red-50" onClick={() => cancel(c)} disabled={busy === c.id} title="Sepeti kapat (satış yapılmaz)" data-testid={`live-cart-cancel-${c.id}`}><X className="h-4 w-4" /></Button>}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
